import { zodResolver } from '@hookform/resolvers/zod'
import { type GoogleLoginProps } from '@react-oauth/google'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { jwtDecode } from 'jwt-decode'
import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { signInMutation } from '@/lib/queries/business/v1/auth/login/mutations'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'
import { type GoogleLoginCredential } from '@/lib/types/google-login'

import { type AccountVerificationCardProps } from './account-verification-card'
import { deleteAccountFormSchema } from './constants'
import { type DeleteAccountFormSchema } from './types'

export type UseAccountVerificationCardParams = Pick<AccountVerificationCardProps, 'onSuccess'>

export const useAccountVerificationCard = ({ onSuccess }: UseAccountVerificationCardParams) => {
  const cardWrapperRef = useRef<HTMLDivElement | null>(null)
  const cardContentRef = useRef<HTMLDivElement | null>(null)

  const navigate = useNavigate()

  const { data: dataUser } = useQuery(getUserQuery)
  const { mutateAsync: signIn, isPending: isVerifyingUser } = useMutation(signInMutation)

  const form = useForm<DeleteAccountFormSchema>({
    mode: 'onChange',
    resolver: zodResolver(deleteAccountFormSchema),
    defaultValues: {
      currentPassword: '',
    },
  })

  const handleSubmitForm = async (data: DeleteAccountFormSchema) => {
    const userEmail = dataUser?.user.email
    const userInputPassword = data.currentPassword
    if (!userEmail) {
      return
    }

    await signIn(
      {
        email: userEmail,
        password: userInputPassword,
      },
      {
        onSuccess: () => {
          onSuccess()
          toast.success('Verification succeeded!', {
            duration: 5000,
          })
        },
        onError: () => {
          toast.error('Failed to verify', {
            description: 'Please check the password or try again later.',
            duration: 5000,
          })
        },
      }
    )
  }

  const handleClickCancelButton = () => {
    void navigate({ to: '/settings' })
  }

  const handleSuccessGoogleLogin: GoogleLoginProps['onSuccess'] = (credentialResponse) => {
    if (!credentialResponse.credential) {
      toast.error('Something went wrong with Google sign in', {
        description: 'Please try again later.',
        duration: 6000,
      })
      return
    }

    const decoded: GoogleLoginCredential = jwtDecode(credentialResponse.credential)
    const googleEmail = decoded?.email
    if (dataUser?.user.email === googleEmail) {
      toast.success('Verification succeeded!', {
        duration: 6000,
      })
      onSuccess()
      return
    } else {
      toast.error('Sign-in info mismatch', {
        description: 'Please use the same account you used to sign in.',
      })
    }
  }

  const handleErrorGoogleLogin: GoogleLoginProps['onError'] = () => {
    console.log('Login Failed')
  }

  const formErrors = form.formState.errors
  const formValues = form.getValues()

  const cardWrapperWidth = cardWrapperRef.current?.clientWidth ?? 0
  const cardWrapperSideMargin = cardWrapperRef.current
    ? parseInt(window.getComputedStyle(cardWrapperRef.current)?.marginInline)
    : 0
  const cardContentSidePadding = cardContentRef.current
    ? parseInt(window.getComputedStyle(cardContentRef.current)?.paddingInline)
    : 0

  const isConfirmButtonDisabled = !formValues.currentPassword
  const isConfirmButtonLoading = isVerifyingUser
  const isCancelButtonDisabled = isVerifyingUser

  return {
    form,
    formErrors,
    cardWrapperRef,
    cardWrapperWidth,
    cardWrapperSideMargin,
    cardContentRef,
    cardContentSidePadding,
    isConfirmButtonLoading,
    isConfirmButtonDisabled,
    isCancelButtonDisabled,
    handleSubmitForm,
    handleClickCancelButton,
    handleSuccessGoogleLogin,
    handleErrorGoogleLogin,
  }
}
